import { createRoot } from 'react-dom/client'
import { installAiShim } from "./lib/ai-shim"
import "./i18n"

import "./main.css"
import "./styles/theme.css"
import "./index.css"

const BOOT_TIMEOUT_MS = 8000;

function paintBootPlaceholder(message: string): void {
  if (typeof document === 'undefined') return;
  const root = document.getElementById('root');
  if (!root) return;

  root.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;background:#f8fafc;color:#0f172a;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;">
      <div style="max-width:560px;width:100%;background:#ffffff;border:1px solid #e2e8f0;border-radius:12px;padding:20px;box-shadow:0 6px 24px rgba(15,23,42,0.08);">
        <h1 style="margin:0 0 8px;font-size:20px;line-height:1.2;font-weight:700;">BBA Services is starting…</h1>
        <p style="margin:0;font-size:14px;line-height:1.5;color:#334155;">${message}</p>
      </div>
    </div>
  `;
}

function paintBootFailure(details: string): void {
  if (typeof document === 'undefined') return;
  const root = document.getElementById('root');
  if (!root) return;

  root.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;background:#fff7ed;color:#7c2d12;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;">
      <div style="max-width:640px;width:100%;background:#ffffff;border:1px solid #fed7aa;border-radius:12px;padding:20px;box-shadow:0 6px 24px rgba(124,45,18,0.12);">
        <h1 style="margin:0 0 8px;font-size:20px;line-height:1.2;font-weight:700;">We hit a startup issue</h1>
        <p style="margin:0 0 12px;font-size:14px;line-height:1.5;color:#9a3412;">The app could not finish loading. Please refresh and try again.</p>
        <pre style="margin:0;padding:12px;background:#fff1e6;border:1px solid #fed7aa;border-radius:8px;font-size:12px;line-height:1.4;white-space:pre-wrap;word-break:break-word;">${details}</pre>
      </div>
    </div>
  `;
}

function shouldLoadSparkSDK(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host.includes('spark') || host.includes('github.app');
}

async function bootstrapApp(): Promise<void> {
  paintBootPlaceholder('Initializing secure workspace and loading your dashboard.');

  let bootTimedOut = false;
  const bootTimeout = typeof window !== 'undefined'
    ? window.setTimeout(() => {
        bootTimedOut = true;
        paintBootPlaceholder('Startup is taking longer than expected. This page will continue loading automatically.');
      }, BOOT_TIMEOUT_MS)
    : undefined;

  if (shouldLoadSparkSDK()) {
    try {
      await import('@github/spark/spark');
    } catch (error) {
      console.warn('[bootstrap] Spark SDK failed to load; continuing with AI shim fallback.', error);
    }
  }

  installAiShim();

  const [
    { ErrorBoundary },
    { BrowserRouter },
    { default: App },
    { AuthProvider },
    { ErrorFallback },
  ] = await Promise.all([
    import('react-error-boundary'),
    import('react-router-dom'),
    import('./App.tsx'),
    import('./contexts/AuthContext.tsx'),
    import('./ErrorFallback.tsx'),
  ]);

  try {
    createRoot(document.getElementById('root')!).render(
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    );
  } catch (error) {
    const details = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    paintBootFailure(details);
    throw error;
  } finally {
    if (bootTimeout !== undefined) {
      window.clearTimeout(bootTimeout);
    }

    if (bootTimedOut) {
      console.warn('[bootstrap] App recovered after delayed startup.');
    }
  }
}

void bootstrapApp();
